import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { api } from "../lib/api";
import { useCurrentCompany } from "../components/CompanySwitcher";
import { fmtMoney, fmtDate } from "../lib/format";

interface ParsedRow {
  customer: string;
  date: string; // YYYY-MM-DD
  number: string;
  status?: string;
  taxable?: number;
  tax?: number;
  amount: number;
  memo?: string;
}

// Parse a QBO "Estimates by Customer" worksheet (the layout in our seed data file).
function parseSheet(ws: XLSX.WorkSheet): ParsedRow[] {
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: false, defval: null });
  const out: ParsedRow[] = [];
  let currentCustomer = "";

  // Find header row — row containing 'Date' in column B and 'Number' in column C
  let dataStartIdx = -1;
  for (let i = 0; i < Math.min(aoa.length, 20); i++) {
    const r = aoa[i] || [];
    if (String(r[1] || "").trim().toLowerCase() === "date" && String(r[2] || "").trim().toLowerCase() === "number") {
      dataStartIdx = i + 1;
      break;
    }
  }
  if (dataStartIdx < 0) return out;

  for (let i = dataStartIdx; i < aoa.length; i++) {
    const row = aoa[i] || [];
    const colA = row[0] == null ? "" : String(row[0]).trim();
    const colB = row[1] == null ? "" : String(row[1]).trim();

    // Customer header: col A populated, col B empty, not a "Total for" subtotal.
    if (colA && !colB && !/^total for /i.test(colA)) {
      currentCustomer = colA;
      continue;
    }
    if (/^total for /i.test(colA)) continue;

    // Detail row: column B is a date, column C is the estimate number.
    if (!colB || !row[2]) continue;
    const isoDate = parseDateToIso(colB);
    if (!isoDate) continue;

    out.push({
      customer: currentCustomer,
      date: isoDate,
      number: String(row[2]).trim(),
      status: row[3] ? String(row[3]).trim() : undefined,
      taxable: numOrZero(row[4]),
      tax: numOrZero(row[5]),
      amount: numOrZero(row[6]),
      memo: row[7] ? String(row[7]) : undefined,
    });
  }
  return out;
}

function numOrZero(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const n = parseFloat(String(v).replace(/[, ]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function parseDateToIso(s: string): string | null {
  // QBO export uses DD/MM/YYYY (Thailand locale). Accept that and a couple of fallbacks.
  const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
  if (m) {
    let [_, d, mo, y] = m;
    if (y.length === 2) y = (Number(y) > 50 ? "19" : "20") + y;
    return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
}

type ImportResult = {
  batch_id: number; new_clients: number; new_estimates: number;
  updated_estimates: number; closed_estimates: number; row_count: number;
};

export default function Import() {
  const { current } = useCurrentCompany();
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [filename, setFilename] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [autoClose, setAutoClose] = useState(true);
  const [importing, setImporting] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<ImportResult | null>(null);
  const successRef = useRef<HTMLDivElement | null>(null);

  // Elapsed-seconds counter shown next to the spinner so the user knows it's alive.
  useEffect(() => {
    if (!importing) { setElapsed(0); return; }
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [importing]);

  // Auto-scroll to the success banner once we get a result.
  useEffect(() => {
    if (result && successRef.current) {
      successRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  async function handleFile(f: File) {
    setError(null); setResult(null);
    setFilename(f.name);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const parsed = parseSheet(ws);
      if (!parsed.length) {
        setError("No invoice rows found. Make sure this is the QBO 'Estimates by Customer' export (QBO calls them estimates internally; we treat them as invoices).");
      }
      setRows(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to parse file");
    }
  }

  async function doImport() {
    if (!current || !rows.length) return;
    setImporting(true); setError(null); setResult(null);
    try {
      const r = await api<ImportResult>("/api/import", {
        method: "POST",
        body: JSON.stringify({
          company_id: current.id,
          source_filename: filename,
          rows,
          auto_close_missing: autoClose,
        }),
      });
      setResult(r);
      setRows([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
    } finally {
      setImporting(false);
    }
  }

  function resetForAnotherImport() {
    setResult(null); setError(null); setFilename(null); setRows([]);
  }

  const total = rows.reduce((s, r) => s + r.amount, 0);
  const uniqueCustomers = new Set(rows.map((r) => r.customer)).size;

  return (
    <div className="space-y-4">
      <div className="card p-5">
        <div className="text-base font-semibold">Import invoices</div>
        <p className="mt-1 text-sm text-slate-600">
          Drop the QBO <strong>Estimates by Customer</strong> XLSX export. We parse it in your browser — the file
          itself never leaves the app. Each row becomes an open invoice in the system.
        </p>
        <label className="mt-4 flex h-32 cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100">
          <input
            type="file" accept=".xlsx" className="hidden"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          />
          <div className="text-sm text-slate-600">
            {filename ? <><strong>{filename}</strong> &nbsp;·&nbsp; click to choose another</>
                      : "Drop the XLSX here, or click to choose"}
          </div>
        </label>

        {error && <div className="mt-3 rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

        {rows.length > 0 && !result && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Rows" value={String(rows.length)} />
              <Stat label="Customers" value={String(uniqueCustomers)} />
              <Stat label="Total" value={fmtMoney(total, current?.currency || "THB")} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={autoClose} onChange={(e) => setAutoClose(e.target.checked)} />
              Auto-close invoices that are no longer in this file (treat as paid)
            </label>
            <button className="btn-primary" onClick={doImport} disabled={importing}>
              {importing
                ? <><Spinner /> Importing {rows.length} rows… {elapsed}s</>
                : <>Confirm import → {current?.name}</>}
            </button>
            {importing && (
              <div className="text-xs text-slate-500">
                Processing each row in the database — for ~300 rows this can take 15–30 seconds.
                Don't close the tab.
              </div>
            )}
          </div>
        )}
      </div>

      {result && (
        <div ref={successRef} className="card overflow-hidden ring-1 ring-emerald-200">
          <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-white px-5 py-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42L8.5 12.08l6.79-6.79a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            </div>
            <div className="flex-1">
              <div className="font-semibold text-slate-900">Import complete</div>
              <div className="text-xs text-slate-500">
                Batch #{result.batch_id} · {filename || "uploaded file"}
              </div>
            </div>
            <button className="btn-secondary" onClick={resetForAnotherImport}>Import another</button>
          </div>
          <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-5">
            <Stat label="Rows processed" value={String(result.row_count)} />
            <Stat label="New clients" value={String(result.new_clients)} />
            <Stat label="New invoices" value={String(result.new_estimates)} />
            <Stat label="Updated" value={String(result.updated_estimates)} />
            <Stat label="Auto-closed" value={String(result.closed_estimates)} />
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left uppercase text-slate-500">
              <tr><th className="px-3 py-2">Customer</th><th>Date</th><th>Number</th>
                  <th>Status</th><th className="text-right">Amount</th><th>Memo</th></tr>
            </thead>
            <tbody>
              {rows.slice(0, 100).map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-1">{r.customer}</td>
                  <td>{fmtDate(r.date)}</td>
                  <td className="font-mono">{r.number}</td>
                  <td>{r.status}</td>
                  <td className="text-right font-mono">{fmtMoney(r.amount, current?.currency || "THB")}</td>
                  <td className="max-w-md truncate text-slate-500">{r.memo}</td>
                </tr>
              ))}
              {rows.length > 100 && (
                <tr><td colSpan={6} className="px-3 py-2 text-center text-slate-500">
                  …and {rows.length - 100} more rows
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-slate-50 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold tracking-tightish text-slate-900">{value}</div>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin text-current" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
